import { defineMessages, FormattedMessage } from '@faceup/localization'
import { useLocation } from '@faceup/router'
import { twMerge } from '@faceup/ui'
import { useElementSize, useMediaQuery } from '@mantine/hooks'
import { type ReactNode, useEffect, useState } from 'react'
import { useClient } from '../../App/ClientProvider'

import { breakpoints, desktopBreakpoint, maxAppWidth } from '../../utils/constants'
import { useNetworkStatus } from '../../utils/useNetworkStatus'

const messages = defineMessages('followUp.components.header', [
  { id: 'offlineBanner', defaultMessage: 'No internet connection' },
] as const)

type HeaderProps = { children: ReactNode }

export const Header = (props: HeaderProps) => {
  const { children } = props
  const [scrolling, setScrolling] = useState(false)
  const { client } = useClient()
  const networkStatus = useNetworkStatus()
  const { pathname } = useLocation()
  const { ref, height } = useElementSize()
  const isDesktop = useMediaQuery(`(min-width: ${breakpoints[desktopBreakpoint]})`, true)

  const isOfflineBannerVisible =
    client === 'app' && networkStatus === 'offline' && pathname !== '/offline'

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY <= 5 && scrolling) {
        setScrolling(false)
      } else if (window.scrollY > 5 && !scrolling) {
        setScrolling(true)
      }
    }

    window.addEventListener('scroll', handleScroll)

    return () => window.removeEventListener('scroll', handleScroll)
  }, [scrolling])

  return (
    <div
      style={{
        position: 'relative',
        zIndex: 105,
        maxWidth: maxAppWidth,
        width: '100%',
        margin: 'auto',
        background: 'transparent',
      }}
    >
      <div style={{ height, zIndex: 105 }}>
        <div />
      </div>
      <div
        ref={ref}
        className={twMerge(
          'fixed start-0 top-0 z-[110] h-[var(--followup-header-height)] w-full bg-white pt-safe-area-top transition-all duration-300',
          (scrolling || !isDesktop) && 'shadow-[0px_7px_7px_rgba(0,0,0,0.05)]'
        )}
      >
        <div
          className='relative mx-auto flex h-full w-full flex-row flex-nowrap items-center justify-between bg-transparent px-6 [&_h4]:leading-8.5'
          style={{ maxWidth: maxAppWidth }}
        >
          {children}
        </div>
        {isOfflineBannerVisible && (
          <div className='flex w-full justify-center bg-colorErrorBg px-5 py-1.5 font-semibold text-colorError text-sm'>
            <FormattedMessage message={messages.offlineBanner} />
          </div>
        )}
      </div>
    </div>
  )
}
